import {
    Image,
    ImageBackground,
    Pressable,
    ScrollView,
    Text,
    View,
    TouchableWithoutFeedback,
    Modal,
    Linking,
    Platform
} from "react-native";
import React, {useEffect, useState} from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useIsFocused } from "@react-navigation/native";
import * as ImagePicker from 'expo-image-picker';

import {styles, TAB_BAR_HEIGHT} from "./common/styles";
import {WhiteViewHeader} from "./common/WhiteViewHeader";
import {i18n} from "./common/localization";
import {API_URL} from "./common/constants";
import {authenticatedFetch} from "./common/auth";
import {ActivityIndicatorView} from "./common/ActivityIndicatorView";
import {InfoModal} from "./common/InfoModal";
import {ConfirmationModal} from "./common/ConfirmationModal";

import LogoutIcon from "./icons/LogoutIcon";

import luigi from "./assets/luigi.png";
import bg from "./assets/account-bg.png";

export const Account = ({ userName, userData, onLogout }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [totalAmountMinor, setTotalAmountMinor] = useState(0);
    const [paymentGroups, setPaymentGroups] = useState([]);
    const [selectedGroupCounts, setSelectedGroupCounts] = useState({});
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [image, setImage] = useState(null);
    const isFocused = useIsFocused();

    // Error modal
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [infoModalVisible, setInfoModalVisible] = useState(false);
    const [infoModalText, setInfoModalText] = useState("");

    // Confirmation modal
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);

    // Show menu from WhiteViewHeader's children
    const [showMenuWithChildren, setShowMenuWithChildren] = useState(false);
    const canPayDebt = totalAmountMinor > 0;

    // Loads image every time user name is changed
    useEffect(() => {
            AsyncStorage.getItem('userImage' + userName)
                .then((image) => {
                    setImage(image);
                })
        }, [userName]
    )

    // Loads data every time we focus on Account page
    useEffect(() => {
        if (isFocused) {
            checkPaymentReturn().then((handledPaymentReturn) => {
                if (!handledPaymentReturn) {
                    getData();
                }
            });
        }
    }, [isFocused]);

    // Gets user's debt
    async function getData({showError = true} = {}) {
        setIsLoading(true);

        try {
            const response = await authenticatedFetch(API_URL + "/v2/user/payment-debts", {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (!response.ok) {
                throw new Error("Unable to load payment debts");
            }

            const result = await response.json();
            const hasValidGroups = Array.isArray(result["groups"])
                && result["groups"].every((group) =>
                    group
                    && group.id != null
                    && Number.isInteger(group.amountMinor)
                    && Number.isInteger(group.availableCount)
                    && Number.isInteger(group.defaultSelectedCount)
                    && group.amountMinor > 0
                    && group.availableCount >= 0
                    && group.defaultSelectedCount >= 0
                    && group.defaultSelectedCount <= group.availableCount
                );
            if (result["currency"] !== "EUR" || !Number.isInteger(result["totalAmountMinor"]) || !hasValidGroups) {
                throw new Error("Invalid payment debts response");
            }

            const sortedGroups = [...result["groups"]].sort(compareGroupsByDueDate);
            const defaultCounts = {};
            sortedGroups.forEach((group) => {
                defaultCounts[group.id] = group.defaultSelectedCount;
            });
            setTotalAmountMinor(result["totalAmountMinor"]);
            setPaymentGroups(sortedGroups);
            setSelectedGroupCounts(defaultCounts);
            return true;
        } catch (error) {
            resetDebtState();
            if (showError) {
                setInfoModalVisible(false);
                setInfoModalText("");
                setErrorModalVisible(true);
            }
            console.log(error);
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    async function pickImage() {
        // No permissions request is necessary for launching the image library
        let result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.All,
            allowsEditing: true,
            aspect: [1, 1],
            quality: 1,
            base64: true
        });

        if (!result.cancelled) {
            const imageBase64 = result.base64;
            AsyncStorage.setItem('userImage' + userName, imageBase64)
                .then(() => {
                    setImage(imageBase64);
                })
                .catch((error) => {
                    setErrorModalVisible(true);
                    console.log(error);
                })
        }
    }

    // Closes error modal
    function onPressClose() {
        setErrorModalVisible(false);
        setInfoModalVisible(false);
        setInfoModalText("");
        setConfirmModalVisible(false);
    }

    function onPressLogoutMenuItem() {
        setShowMenuWithChildren(false);
        setConfirmModalVisible(true);
    }

    function onConfirmLogout() {
        onPressClose();
        onLogout();
    }

    // Open/close menu from WhiteViewHeader's children
    function onPressMenuIcon() {
        setShowMenuWithChildren(!showMenuWithChildren);
    }

    // Close menu on pressing outside
    function onPressOutside() {
        setShowMenuWithChildren(false);
    }

    function onPressPayNow() {
        setPaymentModalVisible(true);
    }

    function onClosePaymentModal() {
        setPaymentModalVisible(false);
    }

    function resetDebtState() {
        setTotalAmountMinor(0);
        setPaymentGroups([]);
        setSelectedGroupCounts({});
        setPaymentModalVisible(false);
    }

    function changeGroupCount(groupId, delta) {
        const group = paymentGroups.find((item) => item.id === groupId);
        if (!group) return;

        setSelectedGroupCounts((current) => {
            const nextCount = Math.min(
                group.availableCount,
                Math.max(0, (Number.isInteger(current[groupId]) ? current[groupId] : 0) + delta)
            );
            return {...current, [groupId]: nextCount};
        });
    }

    function selectedTotalMinor() {
        return paymentGroups.reduce((total, group) => {
            const count = Number.isInteger(selectedGroupCounts[group.id]) ? selectedGroupCounts[group.id] : 0;
            return total + group.amountMinor * count;
        }, 0);
    }

    function formatAmountMinor(amountMinor) {
        const locale = i18n.locale && i18n.locale.startsWith("it") ? "it-IT" : "en-GB";
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format((Number.isInteger(amountMinor) ? amountMinor : 0) / 100);
    }

    function formatDueDate(dueDate) {
        if (!dueDate) return i18n.t("dueDateUnavailable");

        const parsedDate = new Date(dueDate);
        if (Number.isNaN(parsedDate.getTime())) return dueDate;

        const locale = i18n.locale && i18n.locale.startsWith("it") ? "it-IT" : "en-GB";
        return new Intl.DateTimeFormat(locale, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            timeZone: "UTC",
        }).format(parsedDate);
    }

    function openCheckout(checkoutUrl) {
        if (Platform.OS === "web" && typeof window !== "undefined") {
            if (checkoutUrl.startsWith("/payment-result")) {
                const checkoutParams = new URLSearchParams(checkoutUrl.split("?")[1] || "");
                const orderId = checkoutParams.get("orderId");
                if (orderId) {
                    const currentParams = new URLSearchParams(window.location.search);
                    currentParams.set("orderId", orderId);
                    window.history.pushState({}, "", window.location.pathname + "?" + currentParams.toString() + window.location.hash);
                    checkPaymentReturn();
                    return Promise.resolve();
                }
            }
            window.location.href = resolveCheckoutUrl(checkoutUrl);
            return Promise.resolve();
        }
        return Linking.openURL(resolveCheckoutUrl(checkoutUrl));
    }

    function resolveCheckoutUrl(checkoutUrl) {
        if (/^https?:\/\//i.test(checkoutUrl)) {
            return checkoutUrl;
        }
        if (checkoutUrl.startsWith("/api/")) {
            return API_URL.replace(/\/api\/?$/, "") + checkoutUrl;
        }
        return checkoutUrl;
    }

    async function onPressProceedPayment() {
        const groups = paymentGroups
            .map((group) => ({
                id: group.id,
                selectedCount: Number.isInteger(selectedGroupCounts[group.id]) ? selectedGroupCounts[group.id] : 0,
            }))
            .filter((group) => group.selectedCount > 0);
        if (groups.length === 0) return;

        const expectedTotalAmountMinor = selectedTotalMinor();
        if (expectedTotalAmountMinor <= 0) return;
        setIsLoading(true);

        try {
            const response = await authenticatedFetch(API_URL + "/v2/user/payments", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({groups, expectedTotalAmountMinor})
            });

            if (response.status === 409) {
                setSelectedGroupCounts({});
                setPaymentModalVisible(false);
                await getData({showError: false});
                setInfoModalVisible(false);
                setInfoModalText(i18n.t("paymentAmountsChanged"));
                setErrorModalVisible(true);
                return;
            }
            if (!response.ok) {
                throw new Error("Unable to create payment");
            }

            const result = await response.json();
            if (result["nexiUrl"] && result["nexiParams"]) {
                setPaymentModalVisible(false);
                await submitNexiPayment(result["nexiUrl"], result["nexiParams"]);
                return;
            }
            if (result["checkoutUrl"]) {
                setPaymentModalVisible(false);
                await openCheckout(result["checkoutUrl"]);
                return;
            }
            throw new Error("Invalid payment checkout response");
        } catch (error) {
            setInfoModalText("");
            setErrorModalVisible(true);
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    function submitNexiPayment(nexiUrl, nexiParams) {
        if (Platform.OS !== "web" || typeof document === "undefined") {
            return Linking.openURL(nexiUrl);
        }

        const form = document.createElement("form");
        form.method = "POST";
        form.action = nexiUrl;
        form.acceptCharset = "ISO-8859-1";
        Object.entries(nexiParams).forEach(([name, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.value = value == null ? "" : String(value);
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return Promise.resolve();
    }

    function paymentStatusFromReturn(params) {
        const paymentReturn = params.get("paymentReturn");

        if (paymentReturn === "success") return "PAID";
        if (paymentReturn === "cancel") return "CANCELED";
        if (paymentReturn === "pending") return "PENDING";
        if (paymentReturn === "failure") return "FAILED";
        return null;
    }

    function showPaymentStatus(status) {
        if (status === "PAID") {
            setErrorModalVisible(false);
            setInfoModalText(i18n.t("paymentSuccess"));
            setInfoModalVisible(true);
        } else if (status === "CANCELED") {
            setInfoModalVisible(false);
            setInfoModalText(i18n.t("paymentCanceled"));
            setErrorModalVisible(true);
        } else if (status === "PENDING") {
            setErrorModalVisible(false);
            setInfoModalText(i18n.t("paymentPending"));
            setInfoModalVisible(true);
        } else {
            setInfoModalVisible(false);
            setInfoModalText(i18n.t("paymentFail"));
            setErrorModalVisible(true);
        }
    }

    async function checkPaymentReturn() {
        if (Platform.OS !== "web" || typeof window === "undefined") return false;

        const params = new URLSearchParams(window.location.search);
        const orderId = params.get("orderId");
        if (!orderId) return false;
        const returnStatus = paymentStatusFromReturn(params);
        const nextUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, "", nextUrl);

        setSelectedGroupCounts({});
        setPaymentModalVisible(false);

        if (returnStatus) {
            showPaymentStatus(returnStatus);
            getData({showError: false});
            return true;
        }

        getData({showError: false});
        setInfoModalVisible(false);
        setInfoModalText(i18n.t("paymentStatusUnknown"));
        setErrorModalVisible(true);
        return true;
    }

    return (
        <TouchableWithoutFeedback onPress={onPressOutside}>
            <View style={styles.container}>
                <ImageBackground source={bg} resizeMode="contain" style={{...styles.whiteViewBgImageContainer, width: 271, height: 119}} />
                <View style={{...styles.whiteViewContainer, paddingBottom: TAB_BAR_HEIGHT}}>
                    <WhiteViewHeader showMenuWithChildren={showMenuWithChildren} onPressMenuIcon={onPressMenuIcon}>
                        <Pressable style={styles.account.settingsRow} onPress={onPressLogoutMenuItem}>
                            <LogoutIcon />
                            <Text style={styles.account.settingsRowText}>{i18n.t("logOut")}</Text>
                        </Pressable>
                    </WhiteViewHeader>
                    <ScrollView style={styles.whiteViewScrollView}>
                        <Text style={styles.whiteViewTitleText}>{i18n.t("account")}</Text>
                        <View style={styles.whiteViewScrollViewInnerContainer}>
                            <View style={styles.account.imageContainer}>
                                <Pressable onPress={pickImage}>
                                    <Image source={image ? { uri: 'data:image/jpeg;base64,' + image } : luigi} style={styles.account.image} />
                                </Pressable>
                                <View style={styles.account.debtContainer}>
                                    <View style={styles.account.debtHeaderRow}>
                                        <Text style={{...styles.account.accountItemLabel, lineHeight: 19, textAlign: "right"}}>{i18n.t("debt")}</Text>
                                    </View>
                                    <Text style={styles.account.debtLabel}>{formatAmountMinor(totalAmountMinor)}</Text>
                                    <Pressable
                                        style={{
                                            ...styles.account.payNowButton,
                                            ...(!canPayDebt ? styles.account.payNowButtonDisabled : {})
                                        }}
                                        onPress={onPressPayNow}
                                        disabled={!canPayDebt}
                                    >
                                        <Text style={styles.account.payNowButtonText}>{i18n.t("payNow")}</Text>
                                    </Pressable>
                                </View>
                            </View>
                            <AccountItem label={i18n.t("you")} title={userName} />
                            <AccountItem label={i18n.t("age")} text={userData ? userData["age"] : "-"} />
                            <AccountItem label={i18n.t("mail")} text={userData ? userData["email"] : "-"} />
                            <AccountItem label={i18n.t("phone")} text={userData ? userData["phoneNumber"] : "-"} />
                            <AccountItem label={i18n.t("address")} text={userData ? userData["address"] : "-"} />
                        </View>
                    </ScrollView>
                </View>
                <InfoModal
                    visible={errorModalVisible}
                    onPressClose={onPressClose}
                    text={infoModalText || undefined}
                    isError={true}
                />
                <InfoModal
                    visible={infoModalVisible}
                    onPressClose={onPressClose}
                    text={infoModalText}
                />
                <ConfirmationModal
                    visible={confirmModalVisible}
                    onPressClose={onPressClose}
                    onPressOk={onConfirmLogout}
                    text={i18n.t("sureLogout")}
                    okText={i18n.t("yesLogOut")}
                />
                <PaymentModal
                    visible={paymentModalVisible}
                    groups={paymentGroups}
                    selectedGroupCounts={selectedGroupCounts}
                    selectedTotalMinor={selectedTotalMinor()}
                    onPressClose={onClosePaymentModal}
                    onChangeGroupCount={changeGroupCount}
                    onPressProceedPayment={onPressProceedPayment}
                    formatAmountMinor={formatAmountMinor}
                    formatDueDate={formatDueDate}
                />
                <ActivityIndicatorView isVisible={isLoading} color={"grey"} />
            </View>
        </TouchableWithoutFeedback>
    )
}

const PaymentText = props => (
    <Text {...props} selectable={Platform.OS === "web" ? false : undefined} />
);

const PaymentModal = ({visible, groups, selectedGroupCounts, selectedTotalMinor, onPressClose, onChangeGroupCount, onPressProceedPayment, formatAmountMinor, formatDueDate}) => {
    const canProceed = selectedTotalMinor > 0;

    return (
        <Modal visible={visible} animationType="slide" presentationStyle="fullScreen">
            <View style={styles.account.paymentModal}>
                <View style={styles.account.paymentModalHeader}>
                    <PaymentText style={styles.account.paymentModalTitle}>{i18n.t("payMembership")}</PaymentText>
                    <Pressable style={styles.account.paymentModalClose} onPress={onPressClose}>
                        <PaymentText style={styles.account.paymentModalCloseText}>X</PaymentText>
                    </Pressable>
                </View>
                <ScrollView style={styles.account.paymentDebtList} contentContainerStyle={styles.account.paymentDebtListContent}>
                    {groups.length === 0 && <PaymentText style={styles.account.paymentEmptyText}>{i18n.t("noPaymentDebts")}</PaymentText>}
                    {groups.map((group) => {
                        const selectedCount = Number.isInteger(selectedGroupCounts[group.id]) ? selectedGroupCounts[group.id] : 0;
                        return (
                            <View
                                key={group.id}
                                style={styles.account.paymentDebtRow}
                            >
                                <View style={styles.account.paymentDebtContent}>
                                    <PaymentText style={styles.account.paymentDebtTitle}>{group.label || i18n.t("membershipFee")}</PaymentText>
                                    <PaymentText style={styles.account.paymentDebtMeta}>
                                        {i18n.t("dueDate")}: {formatDueRange(group.firstDueDate, group.lastDueDate, formatDueDate)}
                                    </PaymentText>
                                    <PaymentText style={styles.account.paymentDebtMeta}>
                                        {i18n.t("installmentsAvailable", {count: group.availableCount})}
                                    </PaymentText>
                                </View>
                                <View style={styles.account.paymentDebtAmountColumn}>
                                    <PaymentText style={styles.account.paymentDebtAmount}>{formatAmountMinor(group.amountMinor)}</PaymentText>
                                    <View style={styles.account.paymentQuantityRow}>
                                        <Pressable
                                            style={{
                                                ...styles.account.paymentQuantityButton,
                                                ...(selectedCount <= 0 ? styles.account.paymentQuantityButtonDisabled : {})
                                            }}
                                            onPress={() => onChangeGroupCount(group.id, -1)}
                                            disabled={selectedCount <= 0}
                                        >
                                            <PaymentText style={styles.account.paymentQuantityButtonText}>-</PaymentText>
                                        </Pressable>
                                        <PaymentText style={styles.account.paymentQuantityText}>{selectedCount}</PaymentText>
                                        <Pressable
                                            style={{
                                                ...styles.account.paymentQuantityButton,
                                                ...(selectedCount >= group.availableCount ? styles.account.paymentQuantityButtonDisabled : {})
                                            }}
                                            onPress={() => onChangeGroupCount(group.id, 1)}
                                            disabled={selectedCount >= group.availableCount}
                                        >
                                            <PaymentText style={styles.account.paymentQuantityButtonText}>+</PaymentText>
                                        </Pressable>
                                    </View>
                                </View>
                            </View>
                        )
                    })}
                </ScrollView>
                <View style={styles.account.paymentFooter}>
                    <View>
                        <PaymentText style={styles.account.paymentTotalLabel}>{i18n.t("selectedTotal")}</PaymentText>
                        <PaymentText style={styles.account.paymentTotalValue}>{formatAmountMinor(selectedTotalMinor)}</PaymentText>
                    </View>
                    <Pressable
                        style={{
                            ...styles.account.paymentProceedButton,
                            ...(!canProceed ? styles.account.paymentProceedButtonDisabled : {})
                        }}
                        onPress={onPressProceedPayment}
                        disabled={!canProceed}
                    >
                        <PaymentText style={styles.account.paymentProceedButtonText}>{i18n.t("proceedPayment")}</PaymentText>
                    </Pressable>
                </View>
            </View>
        </Modal>
    )
}

function formatDueRange(firstDueDate, lastDueDate, formatDueDate) {
    if (!firstDueDate || !lastDueDate || firstDueDate === lastDueDate) {
        return formatDueDate(firstDueDate);
    }
    return `${formatDueDate(firstDueDate)} - ${formatDueDate(lastDueDate)}`;
}

function compareGroupsByDueDate(left, right) {
    const leftTime = left.firstDueDate ? Date.parse(left.firstDueDate) : Number.NaN;
    const rightTime = right.firstDueDate ? Date.parse(right.firstDueDate) : Number.NaN;
    const leftMissing = Number.isNaN(leftTime);
    const rightMissing = Number.isNaN(rightTime);

    if (leftMissing && rightMissing) return 0;
    if (leftMissing) return 1;
    if (rightMissing) return -1;
    return leftTime - rightTime;
}

const AccountItem = ({label, title, text}) => {
    return (
        <View style={styles.account.accountItemView}>
            <Text style={styles.account.accountItemLabel}>{label}:</Text>
            { title ? <Text style={styles.account.accountItemTitle}>{title}</Text> : <Text style={styles.account.accountItemText}>{text}</Text> }
        </View>
    )
}
